import type { LayoutCode, LayoutKey, LayoutRow, VirtuosaLayout } from './types';

// Cascade staircase: each row toward the bottom is offset 0.5 col to the right
// of the row above. Approximates the physical geometry of a real computer
// keyboard.
export const ROW_STAGGER: readonly [number, number, number, number] = [
  0.0, 0.5, 1.0, 1.5,
];

export const LEFT_COLS = new Set<number>([0, 1, 2, 3, 4, 5]);
export const RIGHT_COLS = new Set<number>([6, 7, 8, 9, 10, 11]);

// v2.0: A min stretches to 3.61 (Slash + Digit0 + Backslash), accepted
// to preserve full lattice isomorphy (no overrides). All other voicings
// stay at 1.80, so this threshold is only ever reached on A min.
export const MAX_HAND_STRETCH = 3.7;

interface RawKey {
  code: LayoutCode;
  midi: number;
}

// Layout cells indexed by KeyboardEvent.code (canonical, layout-independent).
// v2.0 lattice: origin KeyD = C♯3 (49), E = +3, NE = +4, NW = +1.
// Pure lattice, no overrides, so the same hand shape transposes across
// every key and octave (full isomorphy). Range: F♯2 (42) → F♯5 (78), 37
// distinct notes, 9 doublons (rows 0/3 share the same note set).
export const LAYOUT_RAW: RawKey[][] = [
  // row 0: number row (12 keys)
  [
    { code: 'Digit1', midi: 45 },
    { code: 'Digit2', midi: 48 },
    { code: 'Digit3', midi: 51 },
    { code: 'Digit4', midi: 54 },
    { code: 'Digit5', midi: 57 },
    { code: 'Digit6', midi: 60 },
    { code: 'Digit7', midi: 63 },
    { code: 'Digit8', midi: 66 },
    { code: 'Digit9', midi: 69 },
    { code: 'Digit0', midi: 72 },
    { code: 'Minus', midi: 75 },
    { code: 'Equal', midi: 78 },
  ],
  // row 1: top letter row (12 keys)
  [
    { code: 'KeyQ', midi: 44 },
    { code: 'KeyW', midi: 47 },
    { code: 'KeyE', midi: 50 },
    { code: 'KeyR', midi: 53 },
    { code: 'KeyT', midi: 56 },
    { code: 'KeyY', midi: 59 },
    { code: 'KeyU', midi: 62 },
    { code: 'KeyI', midi: 65 },
    { code: 'KeyO', midi: 68 },
    { code: 'KeyP', midi: 71 },
    { code: 'BracketLeft', midi: 74 },
    { code: 'BracketRight', midi: 77 },
  ],
  // row 2: home row (12 keys, contains origin KeyD = C♯3)
  [
    { code: 'KeyA', midi: 43 },
    { code: 'KeyS', midi: 46 },
    { code: 'KeyD', midi: 49 },
    { code: 'KeyF', midi: 52 },
    { code: 'KeyG', midi: 55 },
    { code: 'KeyH', midi: 58 },
    { code: 'KeyJ', midi: 61 },
    { code: 'KeyK', midi: 64 },
    { code: 'KeyL', midi: 67 },
    { code: 'Semicolon', midi: 70 },
    { code: 'Quote', midi: 73 },
    { code: 'Backslash', midi: 76 },
  ],
  // row 3: bottom row (10 keys)
  [
    { code: 'KeyZ', midi: 42 },
    { code: 'KeyX', midi: 45 },
    { code: 'KeyC', midi: 48 },
    { code: 'KeyV', midi: 51 },
    { code: 'KeyB', midi: 54 },
    { code: 'KeyN', midi: 57 },
    { code: 'KeyM', midi: 60 },
    { code: 'Comma', midi: 63 },
    { code: 'Period', midi: 66 },
    { code: 'Slash', midi: 69 },
  ],
];

export function buildLayout(): VirtuosaLayout {
  const rows: LayoutKey[][] = LAYOUT_RAW.map((rawRow, rowIdx) => {
    const r = rowIdx as LayoutRow;

    return rawRow.map((raw, colIdx) => ({
      code: raw.code,
      midi: raw.midi,
      row: r,
      col: colIdx,
      x: colIdx + ROW_STAGGER[r],
      y: -rowIdx,
      hand: LEFT_COLS.has(colIdx) ? 'left' : 'right',
    }));
  });

  const byMidi = new Map<number, LayoutKey[]>();
  const byCode = new Map<LayoutCode, LayoutKey>();

  for (const row of rows) {
    for (const k of row) {
      byCode.set(k.code, k);
      const list = byMidi.get(k.midi) ?? [];
      list.push(k);
      byMidi.set(k.midi, list);
    }
  }

  return {
    origin: { code: 'KeyD', midi: 49 },
    intervals: { E: 3, NE: 4, NW: 1 },
    rows,
    byMidi,
    byCode,
  };
}
